// ./src/components/features/CreateEventWizard/components/WizardSelectCard.tsx
//
// A single selectable tile used throughout the wizard (event types now; formats,
// RSVP modes, etc. later). The repo has no generic SelectableCard, so this is the
// shared primitive. Big touch target, clear selected state, keyboard-operable.

import React from 'react';
import { Box, Group, Stack, Text, ThemeIcon } from '@mantine/core';

import { AppIcon } from '@/components/generic';

export interface WizardSelectCardProps {
  title: string;
  description?: string;
  /** Emoji shown in the leading badge (takes precedence over icon). */
  emoji?: string;
  /** AppIcon name shown in the leading badge when no emoji is given. */
  icon?: string;
  selected?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  /** Accent color token for the selected state (default brand). */
  color?: string;
  /** Extra content under the description — badges, a note (#62 feature cards). */
  extra?: React.ReactNode;
}

export const WizardSelectCard: React.FC<WizardSelectCardProps> = ({
  title,
  description,
  emoji,
  icon,
  selected = false,
  disabled = false,
  onClick,
  color = 'brand',
  extra,
}) => {
  // ⚠⚠ NOT a <button> (owner, 2026-09-30: "the option to switch to tickets instead was disabled — why then ask if it does
  //   not work?"). `extra` can hold a BUTTON of its own ("Sell tickets instead" on a feature the mode blocks), and a
  //   disabled <button> swallows every click inside it — a button inside a button is invalid HTML besides. So the card is
  //   a role="button" box: it toggles on click / Enter / Space when enabled, and what is inside `extra` always works.
  const activate = () => {
    if (!disabled) {
      onClick?.();
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Only the card itself — a key pressed on a control inside `extra` is that control's.
    if (e.target !== e.currentTarget || (e.key !== 'Enter' && e.key !== ' ')) {
      return;
    }

    e.preventDefault();
    activate();
  };

  return (
    <Box
      role="button"
      tabIndex={disabled ? -1 : 0}
      onClick={activate}
      onKeyDown={onKeyDown}
      data-selected={selected || undefined}
      data-disabled={disabled || undefined}
      aria-pressed={selected}
      aria-disabled={disabled || undefined}
      style={{
        display: 'block',
        width: '100%',
        padding: 'var(--mantine-spacing-md)',
        borderRadius: 'var(--mantine-radius-md)',
        border: `1.5px solid ${
          selected ? `var(--mantine-color-${color}-6)` : 'var(--mantine-color-default-border)'
        }`,
        backgroundColor: selected ? `var(--mantine-color-${color}-light)` : 'var(--mantine-color-body)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'border-color 120ms ease, background-color 120ms ease',
        textAlign: 'left',
      }}
    >
      <Group gap="sm" wrap="nowrap" align={description || extra ? 'flex-start' : 'center'}>
        <ThemeIcon
          variant={selected ? 'filled' : 'light'}
          color={color}
          size={40}
          radius="md"
          style={{ flexShrink: 0, fontSize: 22, opacity: disabled ? 0.55 : 1 }}
        >
          {emoji ? <span aria-hidden>{emoji}</span> : <AppIcon iconName={icon ?? 'Point'} size={20} />}
        </ThemeIcon>
        <Stack gap={2} style={{ minWidth: 0 }}>
          {/* ⓘ Only the title and description dim on a disabled card — a way out in `extra` stays at full strength. */}
          <Text fw={600} size="md" lh={1.2} truncate style={{ opacity: disabled ? 0.55 : 1 }}>
            {title}
          </Text>
          {description && (
            <Text size="xs" c="dimmed" lh={1.3} style={{ opacity: disabled ? 0.55 : 1 }}>
              {description}
            </Text>
          )}
          {extra}
        </Stack>
      </Group>
    </Box>
  );
};
